<template>
  <div class="p-6 min-h-screen flex flex-col items-center  w-full max-w-2xl rounded-lg shadow justify-center bg-gray-900 text-gray-300">
    <!-- class="justify-center " -->
    <Logo class="w-20 h-20 mb-4 mx-auto" />
    <!-- Optional status message -->
    <div v-if="status" class="mb-4 font-medium text-sm text-center">
      {{ status }}
    </div>

    <form @submit.prevent="submit" class="space-y-6 bg-gray-800 p-6 rounded-lg w-full">
      <div>
        <label for="email" class="block text-sm font-medium mb-1">API KEY</label>
        <input id="api-key" v-model="form.apiKey" type="text" required autofocus placeholder="7fd42359c9b675..."
          class="bg-gray-900 w-full px-4 py-2 rounded border border-gray-700 focus:outline-none focus:ring focus:ring-indigo-200 focus:border-indigo-500" />
        <p v-if="form.errors.apiKey" class="text-sm text-red-600 mt-1">{{ form.errors.apiKey }}</p>
        <Toggle v-model="live" v-if="['live', 'staging', ''].includes(environment)" />
        <p v-else class="mt-2">
          Environment: {{ environment }}<br>
        </p>
        <p v-if="environmentURL" style="font-size: xx-small;" class="mt-2 text-wrap">
          ({{ environmentURL }})
        </p>
      </div>

      <div class="flex justify-end">
        <button 
          type="submit" 
          :disabled="form.processing"
          :class="['btn btn-primary', { 'btn-disabled': form.processing }]"
        >
          {{ form.processing ? 'Logging in...' : 'Log in' }}
        </button>
      </div>
    </form>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import axios from 'axios';
import Toggle from '../components/Toggle.vue';
import Logo from './Logo.vue';

const emit = defineEmits(['LoginSuccess']);

const live = ref(true);
const environment = ref('');
const isSettingEnvironment = ref(false);

const urls = {
  URL_LOCAL: "https://pricematch-app.test/api",
  URL_PROD: "https://live.todmarkets.com.au/api",
  URL_STAGING: "https://staging.todmarkets.com.au/api",
  URL_DEV: "https://dev.todmarkets.com.au/api"
};

const status = ref('');
const form = ref({
  apiKey: '',
  processing: false,
  errors: {},
});

// Computed property for environment URL
const environmentURL = computed(() => {
  const envMap = {
    'dev': urls.URL_DEV,
    'staging': urls.URL_STAGING,
    'live': urls.URL_PROD,
    'local': urls.URL_LOCAL
  };
  return envMap[environment.value] || '';
});

watch(() => form.value.apiKey, () => {
  if (!isSettingEnvironment.value) {
    setEnvironment();
  }
});

watch(() => live.value, () => {
  if (!isSettingEnvironment.value) {
    setEnvironment();
  }
});

function setEnvironment() {
  isSettingEnvironment.value = true;
  
  let apiKey = form.value.apiKey.trim();
  let newEnvironment = live.value ? 'live' : 'staging';

  if (apiKey) {
    const parts = apiKey.split('.');
    if (parts.length === 2) {
      newEnvironment = parts[0].toLowerCase();
    }
  }

  environment.value = newEnvironment;
  
  // Update toggle based on environment
  if (newEnvironment === 'staging' && live.value) {
    live.value = false;
  } else if (newEnvironment === 'live' && !live.value) {
    live.value = true;
  }
  
  isSettingEnvironment.value = false;
}

function parseApiKey(key) {
  const parts = key.split('.');
  return parts.length === 2 ? parts[1].trim() : key.trim();
}

async function submit() {
  form.value.processing = true;
  try {
    await saveCredentials(form.value.apiKey);
    form.value.errors = {};
    status.value = 'Login successful!';
    emit('LoginSuccess');
  } catch (error) {
    console.error('An error occurred:', error);
    form.value.errors = { apiKey: ['An unexpected error occurred.'] };
  } finally {
    form.value.processing = false;
  }
}

async function saveCredentials(key) {
  const url = environmentURL.value;
  
  if (!url) {
    status.value = 'MISSING ENVIRONMENT DETAILS';
    throw new Error('Missing environment details');
  }
  
  const cleanKey = parseApiKey(key);
  
  try {
    const creds = await getCreds(cleanKey);
    await OfficeRuntime.storage.setItem('environment', environment.value);
    await OfficeRuntime.storage.setItem('environmentURL', url);
    await OfficeRuntime.storage.setItem('apiKey', cleanKey);
    await OfficeRuntime.storage.setItem('user', JSON.stringify(creds.user));
    await OfficeRuntime.storage.setItem('company', JSON.stringify(creds.company));
  } catch(error) {
    status.value = 'An error occurred while loading credentials. Please try again.';
    form.value.errors = { apiKey: ['Invalid API key or network error.'] };
    throw error;
  }
}

async function fetchData(endpoint, apiKey) {
  try {
    const response = await axios.get(`${environmentURL.value}/${endpoint}/`, {
      headers: {
        'Authorization': `Bearer ${apiKey}`,
      },
    });
    return response.data.data;
  } catch (error) {
    console.error(`An error occurred while fetching ${endpoint} data:`, error);
    throw error;
  }
}

async function getCreds(apiKey) {
  try {
    const [user, company] = await Promise.all([
      fetchData('user', apiKey),
      fetchData('company', apiKey)
    ]);
    return { user, company };
  } catch (error) {
    console.error('An error occurred while fetching credentials:', error);
    throw error;
  }
}

async function autoLogin() {
  const storedKey = await OfficeRuntime.storage.getItem('apiKey');
  const storedEnvironment = await OfficeRuntime.storage.getItem('environment');
  console.log("Checking for stored credentials...");
  
  // Check if all required credentials are stored
  if (storedKey && storedEnvironment) {
    form.value.processing = true;
    try {
      isSettingEnvironment.value = true;
      form.value.apiKey = storedKey;
      // Set the stored environment
      environment.value = storedEnvironment;      
      // Get fresh credentials from API
      const creds = await getCreds(storedKey);
      
      // Update stored user and company data
      await OfficeRuntime.storage.setItem('user', JSON.stringify(creds.user));
      await OfficeRuntime.storage.setItem('company', JSON.stringify(creds.company));
      
      status.value = 'Auto-login successful!';
      emit('LoginSuccess');
    } catch (error) {
      console.error('Auto-login failed:', error);
      status.value = 'Auto-login failed. Please login again.';
      // Clear all stored credentials if auto-login fails
      await OfficeRuntime.storage.removeItem('apiKey');
      await OfficeRuntime.storage.removeItem('environment');
      await OfficeRuntime.storage.removeItem('environmentURL');
      await OfficeRuntime.storage.removeItem('user');
      await OfficeRuntime.storage.removeItem('company');
    } finally {
      isSettingEnvironment.value = false;
      form.value.processing = false;
    }
  } else {
    console.log("Missing stored credentials, user needs to login manually");
    // Set default environment to prod if nothing is stored
    if (!storedEnvironment) {
      environment.value = 'live';
      live.value = true;
    }
  }
}

onMounted(async () => {
  await autoLogin();
});
</script>
